import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { fetchNews } from '../components/NewsBox/NewsBox';
import { useNotifications } from './NotificationContext';

const NewsContext  = createContext(null);
const REFRESH_MS   = 30000;
const SEEN_KEY     = 'pg_seen_news_ids';
const UNREAD_KEY   = 'pg_unread_news';

function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]')); }
  catch { return new Set(); }
}
function saveSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch {}
}

export function NewsProvider({ children }) {
  const [news, setNews]          = useState([]);
  const [loading, setLoading]    = useState(true);
  const [error, setError]        = useState(false);
  const [unreadCount, setUnread] = useState(() =>
    parseInt(localStorage.getItem(UNREAD_KEY) ?? '0', 10)
  );

  // seenIds persisted across remounts/refreshes
  const seenIds     = useRef(loadSeen());
  const isFirstLoad = useRef(true);
  const { push }    = useNotifications();

  const load = useCallback(() => {
    fetchNews()
      .then(data => {
        // TEMP DEBUG — remove once field names are confirmed
        if (data.length) {
          console.log('SAMPLE NEWS ITEM (raw from fetchNews):', JSON.stringify(data[0], null, 2));
        }

        setNews(data);
        setLoading(false);

        if (isFirstLoad.current) {
          // On first load: mark everything already on Monday as seen
          // so we don't fire notifications for old items
          data.forEach(item => seenIds.current.add(item.id));
          saveSeen(seenIds.current);
          isFirstLoad.current = false;
          return;
        }

        // On subsequent polls: only act on truly new IDs
        const newItems = data.filter(item => !seenIds.current.has(item.id));
        newItems.forEach(item => {
          seenIds.current.add(item.id);
          push({
            mondayId: item.id,       // ← dedup key in NotificationContext
            type: 'news',
            title: item.title,
            body: item.body?.slice(0, 80) + (item.body?.length > 80 ? '…' : ''),
            link: '/news',
          });
        });

        if (newItems.length > 0) {
          saveSeen(seenIds.current);
          setUnread(prev => {
            const next = prev + newItems.length;
            localStorage.setItem(UNREAD_KEY, String(next));
            return next;
          });
        }
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [push]);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const clearUnread = useCallback(() => {
    setUnread(0);
    localStorage.setItem(UNREAD_KEY, '0');
  }, []);

  return (
    <NewsContext.Provider value={{ news, loading, error, unreadCount, clearUnread }}>
      {children}
    </NewsContext.Provider>
  );
}

export const useNews = () => useContext(NewsContext);